import deepEquals from "fast-deep-equal";
import React, {useEffect, useRef, useState} from "react";
import {useHistory, useParams} from "react-router-dom";
import {ResponseStatus} from "src/EstateFlowchart/client/EstateFlowchartApiClient";
import FiduciaryColumn from "src/EstateFlowchart/components/FiduciaryColumn/FiduciaryColumn";
import GenericErrorModal, {
    genericEmptyErrorModalData,
    GenericErrorModalData
} from "src/components/Modal/Error/GenericErrorModal";
import LoadingIndicator from "../../../pages/LoadingIndicator";
import {RouteWithId} from "../../../routes/types";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import FormHeader from "../../components/FormHeader/FormHeader";
import {FiduciaryAssignment, FiduciarySummary} from "../../model/EstateFlowchartTypes";
import {familyMembersSelector} from "../../slices/EstateFlowChartSlice";
import {
    getFiduciaries,
    handleSaveFiduciaries,
    initializeFiduciaryState,
    isLivingPartner
} from "../../utils/FiduciaryUtils";
import {EmptyStateContainer} from "../../../components";
import "./_review-fiduciaries.scss";
import {selectReleaseToggles} from "../../../ReleaseToggles/releaseTogglesSlice";
import ScrollableContainer from "../../../components/ScrollableContainer/ScrollableContainer";


const ReviewFiduciariesForm: React.FC = (): JSX.Element => {
    const {id} = useParams<RouteWithId>();
    const history = useHistory();
    const dispatch = useAppDispatch();
    const familyMembers = useAppSelector(familyMembersSelector);
    const [formHasBeenEdited, setFormHasBeenEdited] = useState(false);
    const [headerScrolled, setHeaderScrolled] = useState(false);
    const [error, setError] = useState<GenericErrorModalData>(genericEmptyErrorModalData);
    const [showTempErrorMessage, setShowTempErrorMessage] = useState(false);
    const releaseToggles = useAppSelector(selectReleaseToggles);
    const containerRef = useRef<HTMLDivElement>(null);

    const [fiduciaries, setFiduciaries] = useState<FiduciarySummary>({} as FiduciarySummary);
    const [initialFiduciaryState, setInitialFiduciaryState] = useState<FiduciarySummary>(() =>
        initializeFiduciaryState(familyMembers)
    );

    const openErrorModal = (e: ResponseStatus) => {
        setError({
            isOpen: true,
            header: "Unable to Load This Page",
            message: (
                <>
                    <p>Check your VPN connection and retry.</p>
                    <p>If the problem persists, contact <a href="mailto:GPIITSupport@ntrs.com">GPS Technical
                        Support</a> at GPIITSupport@ntrs.com and provide the following details:</p>
                </>
            ),
            profileId: id,
            time: new Date(),
            errorDetail: `Failed to load Estate Flowchart page (${e.statusCode})`,
            operationId: e.traceId
        })
    }

    const closeErrorModal = () => {
        setError({...error, isOpen: false});
    };


    useEffect(() => {
        getFiduciaries(id, initialFiduciaryState, setFiduciaries, dispatch, setInitialFiduciaryState).then().catch(err => {
            try {
                const parsedError = JSON.parse(err.message);
                openErrorModal(parsedError);
            } catch (parseError) {
                openErrorModal({
                    data: {},
                    isSuccessStatusCode: false,
                    statusCode: 404,
                    traceId: "",
                });
            }
        });
    }, [id, dispatch]);

    useEffect(() => {
        const handleScroll = () => {
            if (containerRef.current) {
                setHeaderScrolled(containerRef.current.scrollTop > 0);
            }
        };
        const refCurrent = containerRef.current;
        if (refCurrent) {
            refCurrent.addEventListener('scroll', handleScroll);
        }
        return () => {
            if (refCurrent) {
                refCurrent.removeEventListener('scroll', handleScroll);
            }
        };
    }, []);

    if (!familyMembers?.primaryMember) {
        return <LoadingIndicator/>;
    }

    const handleSave = async () => {
        await handleSaveFiduciaries(id, fiduciaries, dispatch, true, history); // Pass navigate as true
    };

    function validateFormIsEdited(newFiduciarySummary: FiduciarySummary) {
        if (!deepEquals(newFiduciarySummary, initialFiduciaryState)) {
            setFormHasBeenEdited(true)
        } else {
            setFormHasBeenEdited(false)
        }
    }

    const handleRetryClickInCommunicationErrorModal = () => {
        closeErrorModal();
        getFiduciaries(id, initialFiduciaryState, setFiduciaries, dispatch, setInitialFiduciaryState).then().catch(err => {
            openErrorModal(JSON.parse(err.message));
        });
    }
    const handleCloseClickInCommunicationErrorModal = () => {
        closeErrorModal();
        setShowTempErrorMessage(true);
    }
    if (showTempErrorMessage) {
        return (
            <EmptyStateContainer
                className="no-wpo-summary-placeholder"
                title="Unable to Load This Page"
                size="large"
                description="Try again later."
            />
        )
    }
    if (error.isOpen) {
        return (
            <GenericErrorModal
                errorModalData={error}
                onClickButton={handleRetryClickInCommunicationErrorModal}
                onRequestClose={handleCloseClickInCommunicationErrorModal}
                buttonText="Retry"
                buttonProps={
                    {
                        primary: true,
                        className: 'full-width center-align',
                        iconPosition: 'left',
                        iconName: 'refresh'
                    }
                }
                showAlertIcon={false}
            />
        )
    }

    const Container = releaseToggles?.enableEstateFlowchartMVPRelease
        ? ScrollableContainer
        : 'div';

    return (
        <div className="review-fiduciaries-page">
            <Container id="review-fiduciaries-form-container-scrollable" className="review-fiduciaries-form-container">
                <div className={`review-fiduciaries-header${headerScrolled ? ' scrolled' : ''}`}>
                    <FormHeader title={"Review Fiduciaries"}
                                formHasBeenEdited={formHasBeenEdited}
                                showSaveButton={true}
                                onSaveButtonClick={handleSave}
                    />
                    <div className="review-fiduciaries-subtitle">
                <span>
                    {'Add Fiduciaries by selecting a name from the dropdown or typing a new name if not listed. If multiple people are serving together, add them in the same field. \n'
                        + 'Reorder succession using the drag icon.'}
                </span>
                    </div>
                </div>
                <div className="review-fiduciaries-content">
                    <FiduciaryColumn familyMembers={familyMembers}
                                     fiduciary={fiduciaries?.primaryMember!}
                                     member={familyMembers?.primaryMember!}
                                     key={"fiduciary" + 0}
                                     updateFiduciary={(newValue: FiduciaryAssignment) => {
                                         const newFiduciarySummary = {...fiduciaries, primaryMember: newValue};
                                         setFiduciaries(newFiduciarySummary)
                                         validateFormIsEdited(newFiduciarySummary);
                                     }}
                    />

                    {isLivingPartner(familyMembers) && (
                        <FiduciaryColumn familyMembers={familyMembers}
                                         fiduciary={fiduciaries?.partnerMember!}
                                         member={familyMembers?.partnerMember!}
                                         key={"fiduciary" + 1}
                                         updateFiduciary={(newValue: FiduciaryAssignment) => {
                                             const newFiduciarySummary = {...fiduciaries, partnerMember: newValue};
                                             setFiduciaries(newFiduciarySummary)
                                             validateFormIsEdited(newFiduciarySummary);
                                         }}
                        />
                    )}
                </div>
            </Container>
        </div>
    )
}

export default ReviewFiduciariesForm;

